// Browser-side Blob <-> data-URL codec for copilot screenshots.
//
// Why a shared module: both CopilotController (assistant-message display)
// and actionExecutor (screenshot action handler, spec § 4.2) need to
// convert Blobs to data-URLs. CopilotController also imports
// actionExecutor, so co-locating these helpers in either consumer would
// create a circular import.
//
// Why data-URLs and not blob: URLs: assistant-ui's fromThreadMessageLike
// sanitizer rejects assistant image parts whose `image` field isn't a
// `data:image/{png,jpeg,jpg,gif,webp};base64,...` string — blob URLs from
// URL.createObjectURL are silently dropped. The cost is ~33% extra memory
// per screenshot vs. holding the raw Blob; for the screenshot sizes we
// deal with (scaled Guacamole frames) that is acceptable.

export function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("FileReader failed"));
    reader.readAsDataURL(blob);
  });
}

// Inverse of blobToDataUrl. Used by CopilotController to reconstruct the
// next POST's current_screenshot multipart payload from the prior
// assistant message's screenshot action result (Tier 1.b § 4.1).
//
// Only handles the base64 form `data:<mime>;base64,<b64>` because that's
// the only shape blobToDataUrl produces.
export function dataUrlToBlob(dataUrl) {
  const m = /^data:([^;,]+);base64,(.+)$/.exec(dataUrl);
  if (!m) {
    throw new Error("dataUrlToBlob: not a data: URL with base64 payload");
  }
  const mime = m[1];
  const binary = atob(m[2]);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
